import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Calendar,
  User,
  MessageSquare,
  FileText,
  Download
} from 'lucide-react';
import { getComplaintById, updateComplaintStatus, Complaint } from '../../services/complaintsApi';
import { useRbac } from '../../context';

export function ComplaintDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, hasPermission } = useRbac();
  const [complaint, setComplaint] = useState<Complaint | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [resolution, setResolution] = useState('');

  useEffect(() => {
    if (id) {
      fetchComplaint();
    }
  }, [id]);

  const fetchComplaint = async () => {
    try {
      const response = await getComplaintById(id!);
      if (response.success) {
        setComplaint(response.data.complaint);
        setResolution(response.data.complaint?.resolution || '');
      }
    } catch (error: any) {
      console.error('Error fetching complaint:', error);
      toast.error(error.message || 'Failed to fetch complaint');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!complaint) return;

    setUpdating(true);
    try {
      const response = await updateComplaintStatus(
        complaint._id,
        newStatus,
        resolution,
        undefined
      );

      if (response.success) {
        toast.success('Complaint status updated successfully');
        fetchComplaint(); // Refresh the complaint data
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast.error(error.message || 'Failed to update complaint status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-blue-100 text-blue-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'urgent':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <Clock className="w-4 h-4" />;
      case 'in_progress':
        return <AlertCircle className="w-4 h-4" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4" />;
      case 'closed':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!complaint) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Complaint Not Found</h2>
          <p className="text-gray-600 mb-4">The complaint you're looking for doesn't exist or you don't have permission to view it.</p>
          <Button onClick={() => navigate('/complaints')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Complaints
          </Button>
        </div>
      </div>
    );
  }

  const isAdmin = hasPermission('manageComplaints');
  const canEdit = isAdmin || complaint.raisedBy._id === user?.id;
  const canView = isAdmin || complaint.raisedBy._id === user?.id;

  // Redirect if user doesn't have permission to view this complaint
  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to view this complaint.</p>
          <Button onClick={() => navigate('/complaints')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Complaints
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{complaint.title}</h1>
                <p className="text-sm text-gray-500">Ticket ID: {complaint.ticketId}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Badge className={`${getStatusColor(complaint.status)} px-3 py-1`}>
                {getStatusIcon(complaint.status)}
                <span className="ml-2 font-medium capitalize">{complaint.status.replace('_', ' ')}</span>
              </Badge>
              <Badge className={`${getPriorityColor(complaint.priority)} px-3 py-1`}>
                <span className="font-medium">{complaint.priority.toUpperCase()}</span>
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description Card */}
            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center text-lg">
                  <MessageSquare className="w-5 h-5 mr-3 text-blue-600" />
                  Description
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{complaint.description}</p>
                </div>
              </CardContent>
            </Card>

            {/* Screenshots */}
            {complaint.screenshots && complaint.screenshots.length > 0 && (
              <Card className="shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-lg">
                    <FileText className="w-5 h-5 mr-3 text-green-600" />
                    Screenshots ({complaint.screenshots.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {complaint.screenshots.map((url, index) => (
                      <div key={index} className="relative group">
                        <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden border">
                          <img
                            src={url}
                            alt={`Screenshot ${index + 1}`}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                              if (fallback) fallback.classList.remove('hidden');
                            }}
                          />
                          <div className="hidden w-full h-full flex items-center justify-center text-gray-500 bg-gray-50">
                            <div className="text-center">
                              <FileText className="w-8 h-8 mx-auto mb-2" />
                              <span className="text-sm">Preview not available</span>
                            </div>
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
                          <Button
                            size="sm"
                            variant="secondary"
                            className="opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                            onClick={() => window.open(url, '_blank')}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            View Full Size
                          </Button>
                        </div>
                        <div className="mt-2 text-center">
                          <span className="text-xs text-gray-500">Screenshot {index + 1}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Attachments */}
            {complaint.attachments && complaint.attachments.length > 0 && (
              <Card className="shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-lg">
                    <FileText className="w-5 h-5 mr-3 text-purple-600" />
                    Attachments ({complaint.attachments.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {complaint.attachments.map((url, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              Attachment {index + 1}
                            </div>
                            <div className="text-xs text-gray-500 truncate max-w-xs">
                              {url.split('/').pop()}
                            </div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(url, '_blank')}
                          className="hover:bg-blue-50 hover:border-blue-300"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Resolution */}
            {complaint?.resolution && (
              <Card className="shadow-sm border-green-200 bg-green-50">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-lg text-green-800">
                    <CheckCircle className="w-5 h-5 mr-3 text-green-600" />
                    Resolution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose max-w-none">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{complaint.resolution}</p>
                  </div>
                  {complaint.resolvedBy && (
                    <div className="mt-4 p-3 bg-white rounded-lg border border-green-200">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Resolved by:</span> {complaint.resolvedBy.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        <span className="font-medium">Date:</span> {complaint.resolvedAt ? formatDate(complaint.resolvedAt) : 'Unknown date'}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Complaint Info */}
            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Complaint Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Created</p>
                    <p className="text-sm text-gray-600">{formatDate(complaint.createdAt)}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <User className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Raised by</p>
                    <p className="text-sm text-gray-600">{complaint.raisedBy.name}</p>
                    <p className="text-xs text-gray-500">{complaint.raisedBy.email}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-900">Category</p>
                  <Badge variant="outline" className="mt-1">
                    {complaint.category}
                  </Badge>
                </div>
                
                {complaint.assignedTo && (
                  <div className="flex items-start space-x-3">
                    <User className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Assigned to</p>
                      <p className="text-sm text-gray-600">{complaint.assignedTo.name}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Admin Actions */}
            {isAdmin && (
              <Card className="shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg text-orange-600">Admin Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label htmlFor="resolution" className="text-sm font-medium text-gray-900">
                      Resolution Notes
                    </Label>
                    <Textarea
                      id="resolution"
                      value={resolution}
                      onChange={(e) => setResolution(e.target.value)}
                      placeholder="Add resolution notes..."
                      rows={4}
                      className="mt-2"
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <Button
                      onClick={() => handleStatusUpdate('in_progress')}
                      disabled={updating || complaint.status === 'in_progress'}
                      className="w-full"
                      variant="outline"
                    >
                      {updating ? 'Updating...' : 'Mark In Progress'}
                    </Button>
                    <Button
                      onClick={() => handleStatusUpdate('resolved')}
                      disabled={updating || complaint.status === 'resolved'}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {updating ? 'Updating...' : 'Mark Resolved'}
                    </Button>
                    <Button
                      onClick={() => handleStatusUpdate('closed')}
                      disabled={updating || complaint.status === 'closed'}
                      className="w-full bg-gray-600 hover:bg-gray-700"
                    >
                      {updating ? 'Updating...' : 'Close Ticket'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
